import {
  BULLET_RADIUS,
  BULLET_RANGE,
  BULLET_SPEED,
  CAMO_TICKS,
  DAMAGE_CENTER,
  DAMAGE_EDGE,
  DAMAGE_MID,
  DROP_AMMO_AMOUNT,
  DROP_HEALTH_AMOUNT,
  DROP_TTL_TICKS,
  HITSCAN_RANGE,
  HIT_CENTER_FRAC,
  HIT_MID_FRAC,
  MAX_HEALTH,
  MAX_PICKUPS,
  MAX_PROJECTILES,
  NO_HIT,
  PICKUP_AMMO,
  PICKUP_CAMO,
  PICKUP_HEALTH,
  PICKUP_RADIUS,
  PLAYER_RADIUS,
  RESERVE_MAX,
  GRENADE_FLIGHT_TICKS,
  GRENADE_RADIUS,
  GRENADE_DAMAGE_MAX,
  GRENADE_DAMAGE_MIN,
  GRENADE_MAX,
  GRENADE_CRATE_AMOUNT,
  PROJ_BULLET,
  PROJ_GRENADE,
  TICK_DT,
  PICKUP_GRENADE,
  DROP_SCATTER_SPEED_MIN,
  DROP_SCATTER_SPEED_MAX,
  DROP_SLIDE_FRICTION,
  DROP_SHIELD_TICKS,
} from './constants';
import { isOpaque, type LevelData, isSolid } from './map';
import type { World } from './sim';

// ---- projectiles ------------------------------------------------------------
//
// Bullets are simulated entities (Commando style): they travel at
// BULLET_SPEED, die after BULLET_RANGE or on a wall, and hit the first body
// they touch. Both ends run the same step; only the server (out.authoritative)
// applies damage and records hits/kills.

// Returns the pool slot, or -1 if the pool is full (the bullet is dropped).
export function spawnProjectile(w: World, owner: number, x: number, y: number, angle: number): number {
  for (let i = 0; i < MAX_PROJECTILES; i++) {
    if (w.pAlive[i] === 0) {
      w.pAlive[i] = 1;
      w.pX[i] = x;
      w.pY[i] = y;
      w.pVX[i] = Math.cos(angle) * BULLET_SPEED;
      w.pVY[i] = Math.sin(angle) * BULLET_SPEED;
      w.pOwner[i] = owner;
      w.pDist[i] = 0;
    w.pKind[i] = PROJ_BULLET;
      return i;
    }
  }
  return -1;
}

// Damage for a bullet whose line of flight passes `lateralOffset` px from
// the target's centre: centre mass hurts most, grazes least. The offset is
// measured perpendicular to the flight path (closest approach), not at the
// first point of contact, which would always be the rim.
// A grenade: same pool, PROJ_GRENADE, flies `dist` px in GRENADE_FLIGHT_TICKS
// whatever the distance (a short lob is slow, a long one fast), over
// anything, and bursts when pTtl runs out. Returns the slot or -1.
export function spawnGrenade(w: World, owner: number, x: number, y: number, angle: number, dist: number): number {
  for (let i = 0; i < MAX_PROJECTILES; i++) {
    if (w.pAlive[i] === 1) continue;
    const speed = dist / (GRENADE_FLIGHT_TICKS * TICK_DT);
    w.pAlive[i] = 1;
    w.pKind[i] = PROJ_GRENADE;
    w.pOwner[i] = owner;
    w.pX[i] = x;
    w.pY[i] = y;
    w.pVX[i] = Math.cos(angle) * speed;
    w.pVY[i] = Math.sin(angle) * speed;
    w.pDist[i] = 0;
    w.pTtl[i] = GRENADE_FLIGHT_TICKS;
    w.pRange[i] = dist;
    return i;
  }
  return -1;
}

// The burst: everyone within GRENADE_RADIUS of the point (to the body
// centre) takes damage falling off linearly from the centre to the edge,
// the thrower included; teammates are spared without friendly fire. The
// explosion and the contacts are recorded on both ends (the client draws
// the blast and flashes the bodies); only the server changes health.
function explode(w: World, i: number): void {
  const out = w.out;
  const x = w.pX[i]!;
  const y = w.pY[i]!;
  const owner = w.pOwner[i]!;
  const b = out.boomCount++;
  w.boomX[b] = x;
  w.boomY[b] = y;
  w.boomOwner[b] = owner;
  for (let id = 0; id < w.max; id++) {
    if (w.alive[id] === 0 || w.health[id] === 0 || w.invuln[id]! > 0) continue;
    if (id !== owner && !w.friendlyFire && w.team[id] !== 0 && w.team[id] === w.team[owner]) continue;
    const d = Math.hypot(w.x[id]! - x, w.y[id]! - y);
    if (d >= GRENADE_RADIUS + PLAYER_RADIUS) continue;
    let t = (d - PLAYER_RADIUS) / GRENADE_RADIUS;
    if (t < 0) t = 0;
    if (t > 1) t = 1;
    const damage = Math.round(GRENADE_DAMAGE_MAX - (GRENADE_DAMAGE_MAX - GRENADE_DAMAGE_MIN) * t);
    if (out.hitCount >= w.hitShooter.length) break; // several bursts at once in a crowd: stop recording, never overrun
    const n = out.hitCount++;
    w.hitShooter[n] = owner;
    w.hitVictim[n] = id;
    w.hitDamage[n] = damage;
    if (!out.authoritative) continue;
    const hp = w.health[id]!;
    if (hp > damage) {
      w.health[id] = hp - damage;
    } else {
      w.health[id] = 0;
      const k = out.killCount++;
      w.killVictim[k] = id;
      w.killShooter[k] = owner;
    }
  }
}

export function damageForHit(lateralOffset: number): number {
  if (lateralOffset <= PLAYER_RADIUS * HIT_CENTER_FRAC) return DAMAGE_CENTER;
  if (lateralOffset <= PLAYER_RADIUS * HIT_MID_FRAC) return DAMAGE_MID;
  return DAMAGE_EDGE;
}

const HIT_DIST = PLAYER_RADIUS + BULLET_RADIUS;
const HIT_DIST2 = HIT_DIST * HIT_DIST;
const KIT_HIT_DIST = PICKUP_RADIUS + BULLET_RADIUS;
const KIT_HIT_DIST2 = KIT_HIT_DIST * KIT_HIT_DIST;
const TOUCH_DIST = PLAYER_RADIUS + PICKUP_RADIUS;
const TOUCH_DIST2 = TOUCH_DIST * TOUCH_DIST;

// ---- pickups ----------------------------------------------------------------

// Returns the pool slot, or -1 if the pool is full (the pickup is dropped).
export function spawnPickup(w: World, kind: number, x: number, y: number, amount: number, ttl: number, vx = 0, vy = 0, shield = 0): number {
  for (let i = 0; i < MAX_PICKUPS; i++) {
    if (w.kAlive[i] === 0) {
      w.kAlive[i] = 1;
      w.kKind[i] = kind;
      w.kX[i] = x;
      w.kY[i] = y;
      w.kAmount[i] = amount;
      w.kTtl[i] = ttl > 65535 ? 65535 : ttl;
      w.kVX[i] = vx;
      w.kVY[i] = vy;
      w.kShield[i] = shield;
      return i;
    }
  }
  return -1;
}

// What a dead player leaves behind: a health kit and an ammo crate either
// side of where they fell.
// What a fallen player leaves: a health kit, an ammo crate and the grenades
// they carried, sliding out from the body in different directions (one
// random base angle from `seed`, the rest evenly around it) and shielded
// from bullets for DROP_SHIELD_TICKS.
export function spawnDeathDrops(w: World, x: number, y: number, grenades = 0, seed = 0): void {
  const n = grenades > 0 ? 3 : 2;
  // Tiny deterministic hash of the seed, so tests and both ends agree.
  let h = (Math.imul(seed | 0, 2654435761) + 0x9e3779b9) >>> 0;
  h ^= h >>> 15;
  const base = (h / 4294967296) * Math.PI * 2;
  const speedSpan = DROP_SCATTER_SPEED_MAX - DROP_SCATTER_SPEED_MIN;
  for (let i = 0; i < n; i++) {
    const a = base + (i / n) * Math.PI * 2;
    h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
    const speed = DROP_SCATTER_SPEED_MIN + (h / 4294967296) * speedSpan;
    const vx = Math.cos(a) * speed;
    const vy = Math.sin(a) * speed;
    if (i === 0) spawnPickup(w, PICKUP_HEALTH, x, y, DROP_HEALTH_AMOUNT, DROP_TTL_TICKS, vx, vy, DROP_SHIELD_TICKS);
    else if (i === 1) spawnPickup(w, PICKUP_AMMO, x, y, DROP_AMMO_AMOUNT, DROP_TTL_TICKS, vx, vy, DROP_SHIELD_TICKS);
    else spawnPickup(w, PICKUP_GRENADE, x, y, grenades, DROP_TTL_TICKS, vx, vy, DROP_SHIELD_TICKS);
  }
}

// Ages pickups and lets living players collect the ones they can use. A
// pickup that would do nothing (full health, full reserve) stays on the
// ground for someone else. Server only (authoritative worlds).
export function stepPickups(w: World): void {
  const level = w.level;
  const ts = level.tileSize;
  for (let i = 0; i < MAX_PICKUPS; i++) {
    if (w.kAlive[i] === 0) continue;
    if (w.kTtl[i]! <= 1) {
      w.kAlive[i] = 0;
      continue;
    }
    w.kTtl[i] = w.kTtl[i]! - 1;
    if (w.kShield[i]! > 0) w.kShield[i] = w.kShield[i]! - 1;
    if (!w.out.authoritative) continue;
    // A fresh death drop slides out and brakes; it stops at a wall.
    const vx = w.kVX[i]!;
    const vy = w.kVY[i]!;
    if (vx !== 0 || vy !== 0) {
      const nx = w.kX[i]! + vx * TICK_DT;
      const ny = w.kY[i]! + vy * TICK_DT;
      if (isSolid(level, (nx / ts) | 0, (ny / ts) | 0)) {
        w.kVX[i] = 0;
        w.kVY[i] = 0;
      } else {
        w.kX[i] = nx;
        w.kY[i] = ny;
        w.kVX[i] = Math.abs(vx) < 4 ? 0 : vx * DROP_SLIDE_FRICTION;
        w.kVY[i] = Math.abs(vy) < 4 ? 0 : vy * DROP_SLIDE_FRICTION;
      }
    }

    const kind = w.kKind[i]!;
    const kx = w.kX[i]!;
    const ky = w.kY[i]!;
    for (let id = 0; id < w.max; id++) {
      if (w.alive[id] === 0 || w.health[id] === 0) continue;
      const dx = w.x[id]! - kx;
      const dy = w.y[id]! - ky;
      if (dx * dx + dy * dy >= TOUCH_DIST2) continue;

      if (kind === PICKUP_HEALTH) {
        const hp = w.health[id]!;
        if (hp >= MAX_HEALTH) continue;
        const nh = hp + w.kAmount[i]!;
        w.health[id] = nh > MAX_HEALTH ? MAX_HEALTH : nh;
      } else if (kind === PICKUP_AMMO) {
        // Rounds and a grenade; taken only if either is useful.
        const r = w.reserve[id]!;
        const g = w.grenades[id]!;
        if (r >= RESERVE_MAX && g >= GRENADE_MAX) continue;
        const nr = r + w.kAmount[i]!;
        w.reserve[id] = nr > RESERVE_MAX ? RESERVE_MAX : nr;
        const ng = g + GRENADE_CRATE_AMOUNT;
        w.grenades[id] = ng > GRENADE_MAX ? GRENADE_MAX : ng;
      } else if (kind === PICKUP_GRENADE) {
        const g = w.grenades[id]!;
        if (g >= GRENADE_MAX) continue;
        const ng = g + w.kAmount[i]!;
        w.grenades[id] = ng > GRENADE_MAX ? GRENADE_MAX : ng;
      } else if (kind === PICKUP_CAMO) {
        if (w.camoTicks[id]! > 0) continue; // charges don't stack
        w.camoTicks[id] = CAMO_TICKS;
        // A player with no prop chosen yet gets one assigned by the server
        // (see Room), which then becomes their choice.
      } else {
        continue;
      }
      w.kAlive[i] = 0;
      break;
    }
  }
}

// Moves one bullet forward by `ticks` ticks against walls and range only,
// no player or pickup tests: a client uses it to catch a freshly announced
// remote bullet up to where the server already has it. Anything it should
// have hit meanwhile is the server's call anyway (docs/netcode.md).
export function advanceProjectile(w: World, i: number, ticks: number, dt: number): void {
  const level = w.level;
  const ts = level.tileSize;
  const step = BULLET_SPEED * dt;
  for (let n = 0; n < ticks && w.pAlive[i] === 1; n++) {
    const nx = w.pX[i]! + w.pVX[i]! * dt;
    const ny = w.pY[i]! + w.pVY[i]! * dt;
    w.pX[i] = nx;
    w.pY[i] = ny;
    if (w.pKind[i] === PROJ_GRENADE) {
      w.pTtl[i] = w.pTtl[i]! - 1;
      if (w.pTtl[i] === 0) {
        explode(w, i);
        w.pAlive[i] = 0;
      }
      continue;
    }
    const dist = w.pDist[i]! + step;
    w.pDist[i] = dist;
    if (dist > BULLET_RANGE || isOpaque(level, (nx / ts) | 0, (ny / ts) | 0)) w.pAlive[i] = 0;
  }
}

// Advances every live bullet by dt. Clears and fills w.hit*/w.kill* and the
// counters in w.out. A step (BULLET_SPEED / TICK_RATE ~ 7 px) is far smaller
// than a body, so point sampling at the new position cannot tunnel.
export function stepProjectiles(w: World, dt: number): void {
  const out = w.out;
  out.hitCount = 0;
  out.killCount = 0;
  out.boomCount = 0;
  const level = w.level;
  const ts = level.tileSize;
  const step = BULLET_SPEED * dt;

  for (let i = 0; i < MAX_PROJECTILES; i++) {
    if (w.pAlive[i] === 0) continue;
    const nx = w.pX[i]! + w.pVX[i]! * dt;
    const ny = w.pY[i]! + w.pVY[i]! * dt;
    w.pX[i] = nx;
    w.pY[i] = ny;
    if (w.pKind[i] === PROJ_GRENADE) {
      // In the air: over walls, bodies and kits; bursts when the fuse ends.
      w.pTtl[i] = w.pTtl[i]! - 1;
      if (w.pTtl[i] === 0) {
        explode(w, i);
        w.pAlive[i] = 0;
      }
      continue;
    }
    w.pDist[i] = w.pDist[i]! + step;

    if (w.pDist[i]! > BULLET_RANGE || isOpaque(level, (nx / ts) | 0, (ny / ts) | 0)) {
      w.pAlive[i] = 0;
      continue;
    }

    // Every kit is fragile: a bullet destroys it and stops there, except
    // a fresh death drop under its shield, which bullets pass over.
    let stopped = false;
    for (let k = 0; k < MAX_PICKUPS; k++) {
      if (w.kAlive[k] === 0 || w.kShield[k]! > 0) continue;
      const kdx = nx - w.kX[k]!;
      const kdy = ny - w.kY[k]!;
      if (kdx * kdx + kdy * kdy < KIT_HIT_DIST2) {
        w.kAlive[k] = 0;
        w.pAlive[i] = 0;
        stopped = true;
        break;
      }
    }
    if (stopped) continue;

    const owner = w.pOwner[i]!;
    for (let id = 0; id < w.max; id++) {
      if (id === owner || w.alive[id] === 0 || w.health[id] === 0 || w.invuln[id]! > 0) continue;
      // Team match without friendly fire: bullets pass through teammates
      // (both ends simulate this, so the visual agrees with the server).
      if (!w.friendlyFire && w.team[id] !== 0 && w.team[id] === w.team[owner]) continue;
      const ddx = nx - w.x[id]!;
      const ddy = ny - w.y[id]!;
      const d2 = ddx * ddx + ddy * ddy;
      if (d2 >= HIT_DIST2) continue;

      w.pAlive[i] = 0;
      // Lateral offset of the body centre from the flight line: |cross| / |v|.
      const vx = w.pVX[i]!;
      const vy = w.pVY[i]!;
      const lateral = Math.abs(ddx * vy - ddy * vx) / BULLET_SPEED;
      const damage = damageForHit(lateral);
      // The contact is recorded on both ends (the client flashes the body);
      // only the server changes health and records kills.
      const n = out.hitCount++;
      w.hitShooter[n] = owner;
      w.hitVictim[n] = id;
      w.hitDamage[n] = damage;
      if (out.authoritative) {
        const hp = w.health[id]!;
        if (hp > damage) {
          w.health[id] = hp - damage;
        } else {
          w.health[id] = 0;
          const k = out.killCount++;
          w.killVictim[k] = id;
          w.killShooter[k] = owner;
        }
      }
      break;
    }
  }
}

// ---- hitscan helpers (kept for future instant-hit weapons) ------------------

// Distance along the ray (unit direction) to the first solid tile, or
// maxDist. Classic DDA grid walk; the ray starts inside a non-solid tile.
export function raycastTiles(
  level: LevelData,
  x0: number,
  y0: number,
  dirX: number,
  dirY: number,
  maxDist: number,
): number {
  const ts = level.tileSize;
  let col = (x0 / ts) | 0;
  let row = (y0 / ts) | 0;
  if (isOpaque(level, col, row)) return 0;

  const stepX = dirX > 0 ? 1 : dirX < 0 ? -1 : 0;
  const stepY = dirY > 0 ? 1 : dirY < 0 ? -1 : 0;
  // Distance along the ray to cross one tile on each axis.
  const tDeltaX = stepX === 0 ? Infinity : Math.abs(ts / dirX);
  const tDeltaY = stepY === 0 ? Infinity : Math.abs(ts / dirY);
  // Distance along the ray to the first tile boundary on each axis.
  let tMaxX =
    stepX === 0 ? Infinity : stepX > 0 ? ((col + 1) * ts - x0) / dirX : (col * ts - x0) / dirX;
  let tMaxY =
    stepY === 0 ? Infinity : stepY > 0 ? ((row + 1) * ts - y0) / dirY : (row * ts - y0) / dirY;

  for (;;) {
    let t: number;
    if (tMaxX < tMaxY) {
      t = tMaxX;
      tMaxX += tDeltaX;
      col += stepX;
    } else {
      t = tMaxY;
      tMaxY += tDeltaY;
      row += stepY;
    }
    if (t >= maxDist) return maxDist;
    if (isOpaque(level, col, row)) return t;
  }
}

// Distance along the ray to the near intersection with a circle, or -1 if
// the ray misses (or the hit is beyond maxDist / behind the origin).
export function rayCircle(
  x0: number,
  y0: number,
  dirX: number,
  dirY: number,
  cx: number,
  cy: number,
  r: number,
  maxDist: number,
): number {
  const fx = x0 - cx;
  const fy = y0 - cy;
  const b = fx * dirX + fy * dirY;
  const c = fx * fx + fy * fy - r * r;
  const disc = b * b - c;
  if (disc < 0) return -1;
  const t = -b - Math.sqrt(disc);
  if (t < 0 || t > maxDist) return -1;
  return t;
}

export interface ShotResult {
  hit: number; // player id or NO_HIT
  endX: number;
  endY: number;
}

// Resolves an instant shot from `shooter` along its aim against the given
// positions (lets a server pass rewound history). Walls stop the ray; the
// nearest living player in front of the wall is the hit. No allocation.
export function resolveShot(w: World, shooter: number, px: Float32Array, py: Float32Array, out: ShotResult): void {
  const x0 = w.x[shooter]!;
  const y0 = w.y[shooter]!;
  const dirX = Math.cos(w.aim[shooter]!);
  const dirY = Math.sin(w.aim[shooter]!);

  let dist = raycastTiles(w.level, x0, y0, dirX, dirY, HITSCAN_RANGE);
  let hit = NO_HIT;
  for (let id = 0; id < w.max; id++) {
    if (id === shooter || w.alive[id] === 0 || w.health[id] === 0) continue;
    const t = rayCircle(x0, y0, dirX, dirY, px[id]!, py[id]!, PLAYER_RADIUS, dist);
    if (t >= 0) {
      dist = t;
      hit = id;
    }
  }
  out.hit = hit;
  out.endX = x0 + dirX * dist;
  out.endY = y0 + dirY * dist;
}
